import React, { useEffect, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "../lib/api";
import Logo from "../components/Logo";
import { toast } from "sonner";
import { CheckCircle, ArrowRight } from "@phosphor-icons/react";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) setError("Missing or invalid reset link.");
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password !== confirm) {
      setError("Passwords do not match.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      setDone(true);
      toast.success("Password updated");
      setTimeout(() => navigate("/login", { replace: true }), 1500);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || "Reset failed.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white flex items-center justify-center p-6">
      <div className="w-full max-w-md">
        <Link to="/" className="inline-flex" data-testid="reset-brand">
          <Logo size={44} />
        </Link>
        <div className="mono-font text-[10px] tracking-[0.3em] uppercase text-white/40 mt-8">Session · Reset</div>
        <h1 className="heading-font text-3xl font-black tracking-tighter mt-2">Set a new password.</h1>

        {done ? (
          <div className="mt-8 border border-[#00FF66]/40 p-6" data-testid="reset-done">
            <div className="flex items-center gap-3">
              <CheckCircle size={22} weight="fill" className="text-[#00FF66]" />
              <div className="heading-font text-lg font-bold">Password updated</div>
            </div>
            <p className="text-white/70 text-sm mt-3">Redirecting you to log in…</p>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-8 space-y-4">
            <div>
              <label className="mono-font text-[10px] tracking-[0.3em] uppercase text-white/50">New Password</label>
              <input
                data-testid="reset-password"
                type="password"
                minLength={6}
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-2 w-full bg-transparent border border-white/15 px-4 py-3 mono-font text-sm text-white focus:outline-none focus:border-[#00FF66] transition-colors duration-200"
                placeholder="Minimum 6 characters"
              />
            </div>
            <div>
              <label className="mono-font text-[10px] tracking-[0.3em] uppercase text-white/50">Confirm Password</label>
              <input
                data-testid="reset-confirm"
                type="password"
                minLength={6}
                required
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                className="mt-2 w-full bg-transparent border border-white/15 px-4 py-3 mono-font text-sm text-white focus:outline-none focus:border-[#00FF66] transition-colors duration-200"
              />
            </div>
            {error && (
              <div data-testid="reset-error" className="mono-font text-xs text-[#FF0033] border border-[#FF0033]/40 px-3 py-2">
                {error}
              </div>
            )}
            <button
              type="submit"
              data-testid="reset-submit"
              disabled={submitting || !token}
              className="w-full flex items-center justify-center gap-3 px-5 py-3 bg-[#00FF66] text-black mono-font text-xs uppercase tracking-[0.25em] hover:bg-white transition-colors duration-200 disabled:opacity-60"
            >
              {submitting ? "Updating…" : "Update Password"} <ArrowRight weight="bold" size={14} />
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
